import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiRequestError } from "../api/client";
import { useCurrentCase } from "../hooks/useCurrentCase";
import { DESIGN_VARIANTS, REPROPOSAL_CREDITS, type DesignVariant } from "../../shared/constants";

interface Proposal {
  id: string;
  variant: DesignVariant;
  image_url: string | null;
}

const VARIANT_LABEL: Record<string, string> = Object.fromEntries(
  DESIGN_VARIANTS.map((v) => [v.value, v.label]),
);

// No.8 デザイン詳細確認・微調整。テキスト指示での再提案（300cr）と確定。
export function DesignTunePage() {
  const { caseRow, loading } = useCurrentCase();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Proposal | null>(null);
  const [balance, setBalance] = useState<number>(0);
  const [tune, setTune] = useState({ hero_headline: "", cta_main: "初回相談を予約する", cta_sub: "取扱業務を見る" });
  const [instructions, setInstructions] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [reproposing, setReproposing] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (!caseRow) return;
    Promise.all([
      api.get<{ proposals: Proposal[]; selected_proposal_id: string | null }>(
        `/api/cases/${caseRow.id}/proposals`,
      ),
      api.get<{ balance: number }>("/api/cases/credits/balance"),
    ])
      .then(([p, b]) => {
        const sel = p.proposals.find((x) => x.id === p.selected_proposal_id) ?? p.proposals[0] ?? null;
        setSelected(sel);
        setBalance(b.balance);
      })
      .finally(() => setFetching(false));
  }, [caseRow]);

  const confirm = async () => {
    if (!caseRow) return;
    setError(null);
    setConfirming(true);
    try {
      await api.post(`/api/cases/${caseRow.id}/confirm-design`, { tune });
      navigate("/sitemap");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "確定に失敗しました");
      setConfirming(false);
    }
  };

  const repropose = async () => {
    if (!caseRow) return;
    if (!instructions.trim()) {
      setError("調整のご指示を入力してください");
      return;
    }
    setError(null);
    setReproposing(true);
    try {
      await api.post(`/api/cases/${caseRow.id}/reproposal`, { instructions });
      navigate("/generating");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "再提案に失敗しました");
      setReproposing(false);
    }
  };

  if (loading || fetching) return <div className="page-center">読み込み中...</div>;
  if (!caseRow) return <div className="page-center">案件が見つかりません。</div>;
  if (!selected) {
    return (
      <div className="page-center">
        デザインが選択されていません。
        <button className="btn btn-ghost" onClick={() => navigate("/design")}>
          デザイン選択へ
        </button>
      </div>
    );
  }

  const enough = balance >= REPROPOSAL_CREDITS;

  return (
    <section className="wizard">
      <div className="wizard-head">
        <span className="step-tag">No.8</span>
        <h1>デザイン詳細確認・微調整</h1>
        <p className="muted">
          AIが提案したデザイン案です。ご希望に合わせて調整し、「この内容で確定」いただくと、下層ページ生成・公開のステップに進みます。
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="wizard-grid">
        <div className="card">
          <h2>選択中のデザインプレビュー</h2>
          <span className="tag tag-opt">{VARIANT_LABEL[selected.variant] ?? selected.variant}</span>
          {selected.image_url && (
            <img className="preview-img wide" src={selected.image_url} alt="プレビュー" />
          )}

          <h2>デザインをカスタマイズする（任意）</h2>
          <p className="muted small">
            下記の文言は、上のプレビュー画像のファーストビュー（画面最上部）に反映されます。
          </p>
          <label className="tune-field">
            ヒーローの見出し
            <span className="cta-hint muted small">
              プレビュー上部の一番大きなキャッチコピーです。事務所の第一印象を決める文言を入力してください。
            </span>
            <input
              value={tune.hero_headline}
              onChange={(e) => setTune((t) => ({ ...t, hero_headline: e.target.value }))}
              placeholder="例）依頼者の未来を守る、最適なリーガルサポートを。"
            />
          </label>
          <div className="form-row">
            <label className="tune-field">
              メインCTAボタン
              <span className="cta-hint muted small">
                完成サイトで最も重要な行動ボタンの文言です（ファーストビュー中央に大きく表示）。例）初回相談を予約する
              </span>
              <input
                value={tune.cta_main}
                onChange={(e) => setTune((t) => ({ ...t, cta_main: e.target.value }))}
              />
            </label>
            <label className="tune-field">
              サブCTAボタン
              <span className="cta-hint muted small">
                メインボタンの隣に置く補助ボタンの文言です（別ページへの案内など）。例）取扱業務を見る
              </span>
              <input
                value={tune.cta_sub}
                onChange={(e) => setTune((t) => ({ ...t, cta_sub: e.target.value }))}
              />
            </label>
          </div>
        </div>

        <aside className="wizard-aside">
          <div className="card">
            <h2>次のステップ</h2>
            <ol className="step-list">
              <li>
                <strong>デザイン確定</strong>
                <span className="muted small">この内容で確定すると下層ページの生成を開始します。</span>
              </li>
              <li>
                <strong>サイト構成の確認</strong>
                <span className="muted small">生成された各ページ構成を確認します。</span>
              </li>
              <li>
                <strong>承認・お支払い</strong>
                <span className="muted small">承認後、初期費用のご請求（銀行振込）へ進みます。</span>
              </li>
            </ol>
            <button className="btn btn-primary btn-block btn-lg" onClick={confirm} disabled={confirming}>
              {confirming ? "確定中..." : "この内容で確定 →"}
            </button>
          </div>

          <div className="card">
            <h2>デザインを再提案</h2>
            <p className="muted small">
              ご希望をテキストで入力すると、6案を作り直します。再提案には{" "}
              <strong>{REPROPOSAL_CREDITS}クレジット</strong> を消費します（残高 {balance.toLocaleString("ja-JP")}cr）。
            </p>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="例）もっと明るく親しみやすい配色に。写真を大きく使いたい。"
              rows={3}
            />
            <button
              className="btn btn-ghost btn-block"
              onClick={repropose}
              disabled={reproposing || !enough}
            >
              {reproposing
                ? "再提案を依頼中..."
                : `${REPROPOSAL_CREDITS}crで再提案する`}
            </button>
            {!enough && (
              <p className="muted small">
                クレジットが不足しています。ダッシュボードからチャージしてください。
              </p>
            )}
          </div>
        </aside>
      </div>

      <div className="wizard-actions">
        <button className="btn btn-ghost" onClick={() => navigate("/design")}>
          ← トップページ案に戻る
        </button>
      </div>
    </section>
  );
}
